// vorgaengeTableView.ts — reine (DOM-freie) Logik für die gespeicherten
// Präferenzen der Vorgangsliste: Spaltenreihenfolge, Sichtbarkeit, Breiten,
// eingeklappte Spalten — und dazu der Sichtbarkeitsbereich (Persönlich/Team/
// Alle) samt Filtern. Ausgelagert aus VorgaengeMo, damit die Normalisierung
// (und vor allem der Reihenfolge-Merge) in der node-Testumgebung prüfbar bleibt.
//
// Warum eine EIGENE, versionierte Präferenz statt der drei alten Einzel-Scopes
// (vorgaenge-table.column-order/-visibility/-widths): sie wird bewusst als EIN
// Paket gespeichert („Als Standard speichern"), und ein Versionsstempel lässt
// eine geänderte Werkseinstellung (neue Spalte, neue Default-Reihenfolge) auch
// bei Nutzern durchschlagen, die bereits gespeichert haben.
//
// WICHTIG — zwei getrennte Ebenen:
//  * `VorgaengeTableView` ist die SPALTENANSICHT. Nur ihre Abweichung vom
//    Standard öffnet die Speichern-Leiste (siehe `viewsDiffer`).
//  * `VorgaengePrefs` ist das, was tatsächlich GESPEICHERT wird — Ansicht plus
//    Bereich plus Filter.
// Der Unterschied ist Absicht: Filter ändert man ständig im Tagesgeschäft, und
// eine Leiste, die bei jedem Filterklick nach einer globalen Einstellung fragt,
// wäre schlicht Lärm. Wer Bereich und Filter dauerhaft will, speichert sie
// ausdrücklich über das Rechtsklick-Menü.

/** Reorder-/ausblendbare (nicht-pinned) Spalten in Werkseinstellungs-Reihenfolge. */
export const VORGAENGE_COLUMN_IDS = [
    "Kontakt",
    // PO 01.11.2026: offener Austausch mit Namen im Hover — seit dem Abend
    // desselben Tages werksseitig AUSGEBLENDET (das Kennzeichen steht wieder
    // unter dem Betreff, gefiltert wird im Filter „Art & Betreff").
    "Austausch",
    "Verantwortung",
    "Beteiligte",
    "TWV",
    "Frist",
    "Ersteller",
    "Erstellt",
] as const;

export type VorgaengeColumnId = (typeof VORGAENGE_COLUMN_IDS)[number];

const COLUMN_ID_SET = new Set<string>(VORGAENGE_COLUMN_IDS);

/**
 * Version der Werkseinstellung. Hochzählen, sobald sich Default-Reihenfolge,
 * Default-Sichtbarkeit oder der Spaltenbestand ändert — gespeicherte Ansichten
 * einer älteren Version werden dann verworfen und der neue Standard greift.
 */
export const VORGAENGE_VIEW_VERSION = 2;

/** Gespeicherte Tabellenansicht (ohne Filter — die sind bewusst sitzungsgebunden). */
export interface VorgaengeTableView {
    order: string[];
    /** Nur ausgeblendete Spalten (`false`); sichtbare fehlen. */
    visibility: Record<string, boolean>;
    /** Gezogene Breiten in px. */
    widths: Record<string, number>;
    /** Schmal eingeklappte Spalten. */
    collapsed: string[];
}

/**
 * Werkseinstellung: „Ersteller" und „Austausch" ausgeblendet (selten
 * gebraucht; der Austausch steht ohnehin als Kennzeichen unter dem Betreff),
 * „Erstellt" (Erstellungsdatum) sichtbar, aber eingeklappt — als schmaler
 * Streifen jederzeit einen Klick entfernt, ohne die Zeile zu verbreitern.
 */
export const DEFAULT_VORGAENGE_VIEW: VorgaengeTableView = {
    order: [...VORGAENGE_COLUMN_IDS],
    visibility: { Ersteller: false, Austausch: false },
    widths: {},
    collapsed: ["Erstellt"],
};

const isKnown = (id: unknown): id is string => typeof id === "string" && COLUMN_ID_SET.has(id);

/**
 * Spalten, für die eine gezogene Breite gespeichert werden darf — bewusst eine
 * ANDERE Menge als {@link VORGAENGE_COLUMN_IDS}: „Betreff" ist `pinned`/`frozen`,
 * also weder ausblendbar noch umsortierbar, aber sehr wohl ziehbar
 * (`resizable: true`).
 *
 * Fehlte „Betreff" hier, verwarf `normalizeVorgaengeView` die gezogene Breite —
 * mit einem besonders unangenehmen Verlauf: das Ziehen wirkte sofort, die
 * Speichern-Leiste erschien aber NIE (der Normalform-Vergleich sah keine
 * Abweichung, weil die Breite auf beiden Seiten herausfiel), und beim nächsten
 * Laden war sie wieder weg.
 */
const WIDTH_COLUMN_IDS: readonly string[] = [...VORGAENGE_COLUMN_IDS, "Betreff"];

const WIDTH_ID_SET = new Set<string>(WIDTH_COLUMN_IDS);

const isWidthKnown = (id: unknown): id is string =>
    typeof id === "string" && WIDTH_ID_SET.has(id);

/** Tiefe Kopie der Werkseinstellung (nie die Konstante selbst mutieren lassen). */
export const defaultVorgaengeView = (): VorgaengeTableView => ({
    order: [...DEFAULT_VORGAENGE_VIEW.order],
    visibility: { ...DEFAULT_VORGAENGE_VIEW.visibility },
    widths: {},
    collapsed: [...DEFAULT_VORGAENGE_VIEW.collapsed],
});

/**
 * Bringt eine Ansicht in Normalform: nur bekannte Spalten-Ids, Reihenfolge ohne
 * Duplikate und vollständig (fehlende Spalten in Werksreihenfolge angehängt),
 * Sichtbarkeit nur mit `false`-Einträgen, Breiten nur mit endlichen positiven
 * Zahlen, eingeklappte Spalten dedupliziert.
 *
 * Die Normalform ist zugleich die Vergleichsform: nur so lässt sich zuverlässig
 * entscheiden, ob die Ansicht vom gespeicherten Standard abweicht — sonst gölte
 * `{Ersteller: true}` gegenüber `{}` als Änderung, obwohl beides „sichtbar" heißt.
 */
export function normalizeVorgaengeView(input: Partial<VorgaengeTableView> | null | undefined): VorgaengeTableView {
    const order: string[] = [];
    const seen = new Set<string>();
    for (const id of input?.order ?? []) {
        if (isKnown(id) && !seen.has(id)) {
            seen.add(id);
            order.push(id);
        }
    }
    // Spalten, die eine gespeicherte Reihenfolge schon kennt. Nur für sie gilt
    // „fehlt in `visibility` = sichtbar"; siehe die Sichtbarkeit unten.
    const gespeichertBekannt = order.length > 0 ? new Set(seen) : null;
    // Eine Spalte, die die gespeicherte Ansicht noch nicht kennt (neu: der
    // „Austausch", 01.11.2026), steht hinter ihrem Vorgänger aus der
    // Werkseinstellung — nicht am Ende, wo sie hinter „Erstellt" niemand sähe.
    // Ohne Versionssprung, der jedem seine gezogenen Breiten nähme.
    VORGAENGE_COLUMN_IDS.forEach((id, i) => {
        if (seen.has(id)) return;
        seen.add(id);
        let nach = -1;
        for (let j = i - 1; j >= 0 && nach < 0; j--) nach = order.indexOf(VORGAENGE_COLUMN_IDS[j]);
        if (nach < 0 && i > 0) order.push(id);
        else order.splice(nach + 1, 0, id);
    });

    // Eine Spalte, die die gespeicherte Ansicht noch nicht kannte, bekommt die
    // Werks-Sichtbarkeit — gespeichert werden ja nur `false`-Einträge, ein
    // Fehlen hieße sonst „sichtbar". So bleibt der „Austausch" für jeden aus,
    // der vor dem 01.11.2026 gespeichert hat, während eine Ansicht, die ihn
    // schon kennt (und nicht ausgeblendet hat), ihn weiter zeigt.
    const visibility: Record<string, boolean> = {};
    for (const id of VORGAENGE_COLUMN_IDS) {
        if (input?.visibility?.[id] === false) visibility[id] = false;
        else if (gespeichertBekannt && !gespeichertBekannt.has(id) && DEFAULT_VORGAENGE_VIEW.visibility[id] === false) {
            visibility[id] = false;
        }
    }

    const widths: Record<string, number> = {};
    for (const [id, w] of Object.entries(input?.widths ?? {})) {
        if (isWidthKnown(id) && typeof w === "number" && Number.isFinite(w) && w > 0) widths[id] = Math.round(w);
    }

    const collapsed: string[] = [];
    for (const id of input?.collapsed ?? []) {
        if (isKnown(id) && !collapsed.includes(id)) collapsed.push(id);
    }

    return { order, visibility, widths, collapsed };
}

/* ────────── Bereich + Filter ────────── */

/** Sichtbarkeitsbereich der Liste. */
export const VORGAENGE_SCOPES = ["alle", "team", "persoenlich"] as const;
export type VorgaengeScope = (typeof VORGAENGE_SCOPES)[number];

const isScope = (v: unknown): v is VorgaengeScope =>
    typeof v === "string" && (VORGAENGE_SCOPES as readonly string[]).includes(v);

/**
 * Der speicherbare Filterzustand der Liste. Bewusst OHNE Suchtext: eine Suche
 * ist eine Frage, keine Voreinstellung — sie beim nächsten Öffnen wieder
 * vorzufinden, sähe nach einer leeren Liste aus.
 */
export interface VorgaengeFilters {
    filterValues: Record<string, string | boolean>;
    dateRangeValues: Record<string, { von?: string; bis?: string }>;
    peopleValues: Record<string, string[]>;
    boFoValue: { boTeamIds: string[]; foUserIds: string[] };
    // Der Vorfilter „Nächste 14 Tage" (`vorfilter14`) ist seit 01.11.2026
    // entfallen (PO). Ältere gespeicherte Filter tragen ihn noch — er wird beim
    // Lesen verworfen und schaltet nichts mehr ein.
    /** Spalten-Sortierung; `null` = Standard „neueste zuerst". */
    sort: { field: string; direction: "asc" | "desc" } | null;
}

export const defaultVorgaengeFilters = (): VorgaengeFilters => ({
    filterValues: {},
    dateRangeValues: {},
    peopleValues: {},
    boFoValue: { boTeamIds: [], foUserIds: [] },
    sort: null,
});

/**
 * Werkseinstellung des Statusfilters: nur OFFENE Vorgänge (T-156, User
 * 26.10.2026). Bis dahin zeigte die Liste ohne Statusfilter offene UND
 * erledigte (PO 02.08.2026) — mit der Zeit „viel zu lang". Der Server bleibt
 * dabei: ohne Statusfilter liefert er beides, erledigte zuletzt.
 */
export const STANDARD_STATUS_FILTER = "Offen";

/**
 * „Alle" ausdrücklich: Offen ODER Erledigt. Ein leerer Statusfilter fiele beim
 * nächsten Öffnen wieder auf die Werkseinstellung zurück — wer alles sehen
 * will, behielte das sonst nur bis zum nächsten Seitenwechsel.
 */
export const ALLE_STATUS = "Offen,Erledigt";

/**
 * Fehlt der Statusfilter, gilt die Werkseinstellung. Greift beim frischen
 * Öffnen, bei einer gespeicherten Ansicht ohne Statusfilter (vor T-156
 * gespeichert) und beim Rücksprung aus dem Detail. Ein gesetzter Wert —
 * auch „Erledigt" oder „Alle" — bleibt, wie er ist.
 */
export function mitStandardStatus<T extends string | boolean | undefined>(
    filterValues: Record<string, T>,
): Record<string, T | string> {
    const status = filterValues.Status;
    if (typeof status === "string" && status.trim() !== "") return filterValues;
    return { ...filterValues, Status: STANDARD_STATUS_FILTER };
}

const strArray = (v: unknown): string[] =>
    Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];

/**
 * Bringt einen Filterzustand in Normalform. Defensiv, weil die Werte aus einer
 * gespeicherten Präferenz kommen: ein umbenannter Filterschlüssel oder ein
 * halber Datumsbereich darf die Liste nicht in einen Zustand bringen, aus dem
 * der Nutzer nicht mehr herausfindet.
 */
export function normalizeVorgaengeFilters(
    input: Partial<VorgaengeFilters> | null | undefined,
): VorgaengeFilters {
    const base = defaultVorgaengeFilters();
    if (!input || typeof input !== "object") return base;

    const filterValues: Record<string, string | boolean> = {};
    for (const [k, v] of Object.entries(input.filterValues ?? {})) {
        if (typeof v === "string" ? v !== "" : typeof v === "boolean") {
            filterValues[k] = v as string | boolean;
        }
    }

    const dateRangeValues: Record<string, { von?: string; bis?: string }> = {};
    for (const [k, v] of Object.entries(input.dateRangeValues ?? {})) {
        if (!v || typeof v !== "object") continue;
        const von = typeof (v as any).von === "string" ? (v as any).von : undefined;
        const bis = typeof (v as any).bis === "string" ? (v as any).bis : undefined;
        if (von || bis) dateRangeValues[k] = { ...(von ? { von } : {}), ...(bis ? { bis } : {}) };
    }

    const peopleValues: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(input.peopleValues ?? {})) {
        const list = strArray(v);
        if (list.length) peopleValues[k] = list;
    }

    const sortIn = input.sort;
    const sort =
        sortIn && typeof sortIn.field === "string" && sortIn.field !== "" &&
        (sortIn.direction === "asc" || sortIn.direction === "desc")
            ? { field: sortIn.field, direction: sortIn.direction }
            : null;

    return {
        filterValues,
        dateRangeValues,
        peopleValues,
        boFoValue: {
            boTeamIds: strArray(input.boFoValue?.boTeamIds),
            foUserIds: strArray(input.boFoValue?.foUserIds),
        },
        sort,
    };
}

/**
 * Ist die Speichern-Leiste sichtbar?
 *
 * Die Regel steht hier und nicht in der Komponente, weil sie der Kern der
 * Bedienung ist: die Leiste darf sich nur bei einer geänderten SPALTENANSICHT
 * von selbst öffnen (oder auf ausdrücklichen Wunsch), und ein „Verwerfen" muss
 * sie zuverlässig schließen, ohne die Änderung zurückzunehmen.
 *
 * @param requested   Über das Rechtsklick-Menü angefordert.
 * @param viewDirty   Spaltenansicht weicht vom gespeicherten Standard ab.
 * @param currentKey  Vergleichsschlüssel der aktuellen Ansicht (`viewKey`).
 * @param dismissedKey Schlüssel, bei dem zuletzt verworfen wurde (`null` = nie).
 */
export function prefsBarVisible(
    requested: boolean,
    viewDirty: boolean,
    currentKey: string,
    dismissedKey: string | null,
): boolean {
    if (requested) return true;
    return viewDirty && dismissedKey !== currentKey;
}

/* ────────── Persistenz ────────── */

/** Was gespeichert wird: Ansicht + Bereich + Filter. */
export interface VorgaengePrefs {
    view: VorgaengeTableView;
    /** `null` = kein gespeicherter Bereich → der Rollen-Default gilt weiter. */
    scope: VorgaengeScope | null;
    /** `null` = keine gespeicherten Filter → die Liste startet wie gehabt. */
    filters: VorgaengeFilters | null;
}

export const defaultVorgaengePrefs = (): VorgaengePrefs => ({
    view: defaultVorgaengeView(),
    scope: null,
    filters: null,
});

/**
 * Persistenzform: Ansicht plus Versionsstempel, dazu Bereich und Filter.
 *
 * Bereich und Filter kamen NACH Version 2 dazu und sind deshalb optional — die
 * Version bleibt bei 2, damit niemand seine gespeicherte Spaltenansicht
 * verliert, nur weil zwei Felder hinzugekommen sind.
 */
export interface StoredVorgaengeView extends VorgaengeTableView {
    v: number;
    scope?: VorgaengeScope;
    filters?: VorgaengeFilters;
}

/** Präferenzen → Persistenzform (immer normalisiert, immer mit aktueller Version). */
export function toStoredView(prefs: VorgaengePrefs | VorgaengeTableView): StoredVorgaengeView {
    // Bestandsaufrufer übergeben noch die nackte Ansicht.
    const p: VorgaengePrefs =
        "view" in prefs ? prefs : { view: prefs, scope: null, filters: null };
    return {
        v: VORGAENGE_VIEW_VERSION,
        ...normalizeVorgaengeView(p.view),
        ...(p.scope ? { scope: p.scope } : {}),
        ...(p.filters ? { filters: normalizeVorgaengeFilters(p.filters) } : {}),
    };
}

/**
 * Persistenzform → Ansicht. Fehlende, kaputte oder veraltete Daten liefern die
 * Werkseinstellung, damit eine geänderte Standardansicht auch bei Bestandsnutzern
 * greift (siehe VORGAENGE_VIEW_VERSION).
 */
export function fromStoredView(raw: unknown): VorgaengeTableView {
    if (!raw || typeof raw !== "object") return defaultVorgaengeView();
    const o = raw as Partial<StoredVorgaengeView>;
    if (o.v !== VORGAENGE_VIEW_VERSION) return defaultVorgaengeView();
    return normalizeVorgaengeView(o);
}

/**
 * Persistenzform → vollständige Präferenzen.
 *
 * Eine veraltete Version verwirft NUR die Ansicht (dort können Spalten
 * weggefallen sein); ein gespeicherter Bereich und gespeicherte Filter bleiben
 * gültig, weil sie nicht an der Spaltenliste hängen.
 */
export function fromStoredPrefs(raw: unknown): VorgaengePrefs {
    if (!raw || typeof raw !== "object") return defaultVorgaengePrefs();
    const o = raw as Partial<StoredVorgaengeView>;
    return {
        view: fromStoredView(raw),
        scope: isScope(o.scope) ? o.scope : null,
        filters: o.filters ? normalizeVorgaengeFilters(o.filters) : null,
    };
}

/** Stabiler Vergleichsschlüssel zweier Ansichten (Normalform, feste Feldreihenfolge). */
export function viewKey(view: VorgaengeTableView): string {
    const n = normalizeVorgaengeView(view);
    const widths = Object.keys(n.widths)
        .sort()
        .map((k) => [k, n.widths[k]] as const);
    const visibility = Object.keys(n.visibility).sort();
    return JSON.stringify([n.order, visibility, widths, [...n.collapsed].sort()]);
}

/** Weichen zwei Ansichten voneinander ab? (Normalform-Vergleich.) */
export function viewsDiffer(a: VorgaengeTableView, b: VorgaengeTableView): boolean {
    return viewKey(a) !== viewKey(b);
}

/**
 * Verschmilzt eine neue Reihenfolge der SICHTBAREN Spalten mit der bisherigen
 * Gesamtreihenfolge.
 *
 * Nötig, weil die DataTable beim Drag-Reorder nur die aktuell sichtbaren Spalten
 * zurückgibt: ausgeblendete Spalten fielen sonst aus der Reihenfolge und die
 * gespeicherte Ansicht wäre unvollständig (genau daran scheiterte die alte
 * Längenprüfung, die eine solche Reihenfolge komplett verwarf und stillschweigend
 * auf den Standard zurückfiel).
 *
 * Verfahren: jede fehlende Spalte wird an ihren bisherigen linken Nachbarn
 * „geheftet" und nach dessen neuer Position wieder eingesetzt; Spalten ohne
 * linken Nachbarn kommen an den Anfang.
 */
export function mergeColumnOrder(prev: string[], nextVisible: string[]): string[] {
    const visible = new Set(nextVisible);
    /** anchorId (oder "" für „ganz vorn") → dahinter einzufügende versteckte Ids. */
    const attached = new Map<string, string[]>();
    let anchor = "";
    for (const id of prev) {
        if (visible.has(id)) {
            anchor = id;
            continue;
        }
        if (!isKnown(id)) continue;
        const list = attached.get(anchor) ?? [];
        list.push(id);
        attached.set(anchor, list);
    }
    const out: string[] = [...(attached.get("") ?? [])];
    for (const id of nextVisible) {
        out.push(id);
        for (const hidden of attached.get(id) ?? []) out.push(hidden);
    }
    // Sicherheitsnetz: nie eine Spalte verlieren (z.B. wenn `prev` unvollständig war).
    for (const id of VORGAENGE_COLUMN_IDS) if (!out.includes(id)) out.push(id);
    return out;
}
